import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ConfirmationDialog from './ConfirmationDialog';

export interface DocumentItem {
  id: string;
  name: string;
  size: number;
  mimeType: string;
  webUrl?: string;
}

interface DocumentsSectionProps {
  documents: DocumentItem[];
  onView?: (document: DocumentItem) => void;
  onDownload?: (document: DocumentItem) => void;
  onDelete?: (document: DocumentItem) => void;
  loading?: boolean;
  deleteLoading?: boolean;
  showActions?: boolean;
  /** When set, the corresponding action button on the row whose id matches
   *  shows a spinner in place of its icon and is disabled. Use when the
   *  consumer fetches a fresh SAS URL on click (view/download) — the spinner
   *  signals that the click was registered. */
  busyDocId?: string | null;
  busyKind?: 'view' | 'download' | null;
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') {
    return <PictureAsPdfIcon sx={{ color: 'error.main', fontSize: 16 }} />;
  }
  if (mimeType.startsWith('image/')) {
    return <ImageIcon sx={{ color: 'info.main', fontSize: 16 }} />;
  }
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <DescriptionIcon sx={{ color: 'primary.main', fontSize: 16 }} />;
  }
  if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
    return <TableChartIcon sx={{ color: 'success.main', fontSize: 16 }} />;
  }
  return <InsertDriveFileIcon sx={{ color: 'text.secondary', fontSize: 16 }} />;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '0 B';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

interface DocumentRowProps {
  document: DocumentItem;
  onView?: (document: DocumentItem) => void;
  onDownload?: (document: DocumentItem) => void;
  onDelete?: (document: DocumentItem) => void;
  showActions?: boolean;
  busyKind?: 'view' | 'download' | null;
}

const DocumentRow = React.memo(function DocumentRow({
  document,
  onView,
  onDownload,
  onDelete,
  showActions = true,
  busyKind = null,
}: DocumentRowProps) {
  const isViewing = busyKind === 'view';
  const isDownloading = busyKind === 'download';
  const anyActionBusy = isViewing || isDownloading;
  const handleView = useCallback(() => {
    onView?.(document);
  }, [document, onView]);

  const handleDownload = useCallback(() => {
    onDownload?.(document);
  }, [document, onDownload]);

  const handleDelete = useCallback(() => {
    onDelete?.(document);
  }, [document, onDelete]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1,
        minHeight: 52,
        borderBottom: '1px solid',
        borderColor: 'rowDivider',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {/* Align the icon with the filename line (not the centre of the name+size block) */}
        <Box sx={{ display: 'flex', alignItems: 'center', alignSelf: 'flex-start', mt: '2px' }}>
          {getFileIcon(document.mimeType)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontWeight: 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 14,
              lineHeight: '20px',
              letterSpacing: '0.15px',
            }}
          >
            {document.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: 10,
              lineHeight: '14px',
            }}
          >
            {formatFileSize(document.size)}
          </Typography>
        </Box>
      </Box>

      {showActions && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ justifyContent: 'flex-end' }}>
          {onView && (
            <Tooltip title={isViewing ? 'Opening…' : 'View'}>
              {/* span wrapper lets Tooltip render against a disabled button */}
              <span>
                <IconButton
                  size="small"
                  onClick={handleView}
                  disabled={anyActionBusy}
                  aria-label={`View ${document.name}`}
                  aria-busy={isViewing}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  {isViewing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <VisibilityOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onDownload && (
            <Tooltip title={isDownloading ? 'Downloading…' : 'Download'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={anyActionBusy}
                  aria-label={`Download ${document.name}`}
                  aria-busy={isDownloading}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  {isDownloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={handleDelete}
                aria-label={`Delete ${document.name}`}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
});

const DocumentsSection = React.memo(function DocumentsSection({
  documents,
  onView,
  onDownload,
  onDelete,
  loading = false,
  deleteLoading = false,
  showActions = true,
  busyDocId = null,
  busyKind = null,
}: DocumentsSectionProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [documentToDelete, setDocumentToDelete] = React.useState<DocumentItem | null>(null);

  const handleDeleteClick = useCallback((document: DocumentItem) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (documentToDelete && onDelete) {
      onDelete(documentToDelete);
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, [documentToDelete, onDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  }, []);

  if (!loading && documents.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No documents uploaded
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          opacity: loading ? 0.6 : 1,
          pointerEvents: loading ? 'none' : 'auto',
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          {documents.map((document) => (
            <DocumentRow
              key={document.id}
              document={document}
              onView={onView}
              onDownload={onDownload}
              onDelete={onDelete ? handleDeleteClick : undefined}
              showActions={showActions}
              busyKind={busyDocId === document.id ? busyKind : null}
            />
          ))}
        </Box>
      </Box>

      <ConfirmationDialog
        open={deleteDialogOpen}
        title="Delete Document"
        message={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={deleteLoading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
});

export default DocumentsSection;
